import { Link } from "react-router-dom";
import { useSesion, type Permiso } from "../lib/sesion";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

/**
 * La pantalla de quien está trabajando.
 *
 * La de inicio de siempre es un informe: cifras del día, gráfico de ingresos,
 * últimas entregas. Eso sirve a quien mira el negocio, y estorba a quien tiene
 * un socio delante y una tablet en la barra. Lo que necesita ahí es lo que hace
 * cien veces al día, en botones que se aciertan con el dedo sin mirar.
 *
 * Por eso el dueño y el trabajador entran a sitios distintos: uno a la foto del
 * club, el otro al mostrador. Es la misma línea que separa los dos roles.
 */

interface Local {
  dentroAhora: number;
  entradasHoy: number;
  /** Máximo de personas del local. 0 = sin fijar, y entonces no se enseña. */
  aforo: number;
}

const ACCIONES: { a: string; clave: Clave; icono: string; permiso: Permiso }[] = [
  { a: "/socios", clave: "il.buscarSocio", icono: "⌕", permiso: "verSocios" },
  { a: "/socios/nuevo", clave: "il.nuevoSocio", icono: "✚", permiso: "crearSocio" },
  { a: "/mostrador", clave: "il.dispensar", icono: "❁", permiso: "dispensar" },
  { a: "/barra", clave: "il.barra", icono: "☕", permiso: "venderBarra" },
];

export default function InicioDelLocal({
  local,
  solicitudesPendientes,
}: {
  local?: Local;
  solicitudesPendientes: number;
}) {
  const { t } = useIdioma();
  const { puede } = useSesion();
  const acciones = ACCIONES.filter((a) => puede(a.permiso));

  const dentro = local?.dentroAhora ?? 0;
  const aforo = local?.aforo ?? 0;
  const lleno = aforo > 0 && dentro >= aforo;

  return (
    <>
      <div className="acciones-local">
        {acciones.map((accion) => (
          <Link key={accion.a} to={accion.a} className="accion-local">
            <span className="icono" aria-hidden="true">
              {accion.icono}
            </span>
            {t(accion.clave)}
          </Link>
        ))}
      </div>

      <div className="cifras-local">
        <div className={`cifra-local${lleno ? " llena" : ""}`}>
          <span className="etiqueta">{t("il.dentroAhora")}</span>
          <strong>
            {dentro}
            {aforo > 0 && <span className="de"> / {aforo}</span>}
          </strong>
          {/*
            Se avisa, no se bloquea. El recuento depende de que la gente marque
            la salida, y cerrarle la puerta a un socio por un número que puede
            estar desajustado sería peor que no tenerlo.
          */}
          {lleno && <span className="nota">{t("il.aforoCompleto")}</span>}
        </div>

        <div className="cifra-local">
          <span className="etiqueta">{t("il.hanPasadoHoy")}</span>
          <strong>{local?.entradasHoy ?? 0}</strong>
        </div>

        {puede("gestionarSolicitudes") && (
          <Link to="/comunidad" className={`cifra-local${solicitudesPendientes > 0 ? " atiende" : ""}`}>
            <span className="etiqueta">{t("il.solicitudes")}</span>
            <strong>{solicitudesPendientes}</strong>
            {solicitudesPendientes > 0 && <span className="nota">{t("il.esperando")}</span>}
          </Link>
        )}

        {puede("gestionarGastos") && (
          <Link to="/gastos" className="cifra-local">
            <span className="etiqueta">{t("il.anotarGasto")}</span>
            <strong>+</strong>
          </Link>
        )}
      </div>
    </>
  );
}
